(async () => {
  if (!customElements.get('country-popup')) {
    const { Fancybox } = await import('@fancyapps/ui');
    const { default: Cookies } = await import('js-cookie');

    class CountryPopup extends HTMLElement {
      constructor() {
        super();

        // Scope
        const self = this;

        // Scope elements
        self.countryPopupWrapper = self.querySelector('[data-country-popup-wrapper]');
        self.buttonStay = self.countryPopupWrapper.querySelector('[data-country-popup-stay]');

        // Set country popup cookie identifier
        self.countryPopupCookieIdentifier = 'country_popup';

        // Default options
        self.defaultOptions = {
          currentCountryCode: '',
          locationApiUrl: 'https://extreme-ip-lookup.com/json',
          expiresInDays: 0,
          locationFieldName: 'countryCode',
          europeanCountries: '',
          usCountries: '',
          gccCountries: '',
          popupMessages: {},
        };

        self.dataOptions = {};

        // Get options from element data and combine with default options
        if (self?.dataset.options) {
          self.dataOptions = JSON.parse(self.dataset.options);
        }

        self.options = {
          ...self.defaultOptions,
          ...self.dataOptions,
        };

        self.init();

        // Listen to extra events when in Shopify editor
        if (Shopify.designMode) {
          // Update the swiper when the section event is triggered
          window.addEventListener('shopify:section:load', (event) => {
            if (event.detail.sectionId == 'country-popup') {
              Fancybox.close();
              self.init();
            }
          });
        }

        // Bind the showPopup method
        this.showPopup = this.showPopup.bind(this);
        this.forceDisplay = false;
      }

      /**
       * Init the country popup plugin
       * @returns void
       */
      init() {
        // Scope
        const self = this;

        // If country popup days is set to zero, remove cookie if exists
        if (parseInt(self.options.expiresInDays) === 0) {
          Cookies.remove(self.countryPopupCookieIdentifier);
        }

        // Get and scope country popup cookie
        self.countryPopupCookie = Cookies.get(self.countryPopupCookieIdentifier);
        if (typeof self.countryPopupCookie == 'undefined') {
          self.countryPopupCookie = 'true';
        }
        self.countryPopupCookie = self.countryPopupCookie === 'true';

        // Render country popup if given
        if (!self.mayRenderCountryPopup()) {
          return false;
        }

        // Get country location
        self.getLocation(function (countryCode) {
          // Render the country popup
          self.renderCountryPopup(countryCode);
        });

        // Button to close popup
        self.buttonStay?.addEventListener('click', self.closeCountryPopup.bind(this));
      }

      /**
       * Check if the country popup may be rendered and that there is a message to show
       * @returns boolean
       */
      mayRenderCountryPopup() {
        // Scope
        const self = this;

        // Guard: check if popup cookie is set
        if (!self.countryPopupCookie) {
          return false;
        }
        // Guard: check if there are messages given
        if (Object.keys(self.options.popupMessages).length == 0) {
          return false;
        }

        return true;
      }

      /**
       * Get country location from cookie or GeoAPI
       * @returns string countryCode
       */
      getLocation(callback) {
        // Scope
        const self = this;

        // Get country code from cookie
        self.countryCode = Cookies.get(self.countryPopupCookieIdentifier + '-country_code');

        // If country code is given, return the code
        if (self.countryCode) {
          return callback(self.countryCode);
        }

        // Get country code from API, if done call the callback
        self.getCountryCodeFromAPI().then((countryCode) => {
          self.setCountryCode(countryCode);
          return callback(countryCode);
        });

        return self.countryCode;
      }

      /**
       * Get country code from API service
       * @returns promise
       */
      getCountryCodeFromAPI() {
        // Scope
        const self = this;

        const defaultApiUrl = '//extreme-ip-lookup.com/json/';
        const countryCodeApi = self.options.locationApiUrl || defaultApiUrl;
        const countryCodeFieldnameIdentifier = self.options.locationFieldName || 'country_code';

        // Guard: get country code API URL from options or get country code field name
        if (countryCodeApi == '' || countryCodeFieldnameIdentifier == '') {
          return self;
        }

        // Get country code from API
        return fetch(countryCodeApi)
          .then((response) => response.json())
          .then((data) => {
            if (
              typeof data[countryCodeFieldnameIdentifier] != 'undefined' &&
              data[countryCodeFieldnameIdentifier] != ''
            ) {
              return data[countryCodeFieldnameIdentifier];
            }
          });
      }

      /**
       * Set country code
       * @return string countryCode
       */
      setCountryCode(countryCode) {
        // Scope
        const self = this;

        // Guard: check country code is given
        if (typeof countryCode == 'undefined' || countryCode == '') {
          return '';
        }

        // Set the country code
        self.countryCode = countryCode;

        // Create a cookie with the country code
        Cookies.set(self.countryPopupCookieIdentifier + '-country_code', countryCode, {
          expires: 1,
        });

        return countryCode;
      }

      /**
       * Update the available languages based on the selected country
       * @returns object selectedCountryOption
       */
      updateAvailableLanguages() {
        const self = this;
        const selectedCountryOption =
          self.countryPopupSelect.options[self.countryPopupSelect.selectedIndex];

        // Get the available languages for the selected country
        const currentAvailableLanguages = selectedCountryOption.dataset.countryLanguages.split(',');

        // Store the currently selected language value
        const currentLanguageValue = self.countryPopupLanguagesSelect.value;

        // Disable all language options and collect available languages
        const availableLanguages = [];

        if (self.countryPopupLanguagesSelect) {
          Array.from(self.countryPopupLanguagesSelect.options).forEach((languageOption) => {
            // Disable the language options by default
            languageOption.disabled = true;

            // Enable the language options that are available for the selected country
            currentAvailableLanguages.forEach((language) => {
              const languageLowerCase = language.toLowerCase();

              if (languageOption.value === languageLowerCase) {
                languageOption.disabled = false;
                availableLanguages.push(languageOption.value);
              }
            });
          });
        }

        // Get the country code in lowercase
        const countryCodeLowerCase = selectedCountryOption.dataset.countryCode.toLowerCase();

        // Initialize newSelectedLanguage
        let newSelectedLanguage;

        // Check if the country code matches an available language
        if (availableLanguages.includes(countryCodeLowerCase)) {
          // Set the language to the country code
          newSelectedLanguage = countryCodeLowerCase;
        } else if (availableLanguages.includes(currentLanguageValue)) {
          // Keep the current language
          newSelectedLanguage = currentLanguageValue;
        } else {
          // Select the first available language
          newSelectedLanguage = availableLanguages[0];
        }

        // Set the selected language
        self.countryPopupLanguagesSelect.value = newSelectedLanguage;

        // Update the confirm button URL
        self.updateConfirmButtonUrl();

        return selectedCountryOption;
      }

      /**
       * Render the country popup
       * @returns void
       */
      renderCountryPopup(countryCode) {
        // Scope
        const self = this;
        self.countryCode = countryCode;

        // Skip this guard if forceDisplay is true
        if (
          (self.countryCode == '' || self.options.currentCountryCode == '') &&
          !self.forceDisplay
        ) {
          return;
        }

        // Set variables for the Country, Languages & VAT switcher elements
        self.countryPopupSelect = self.countryPopupWrapper.querySelector(
          '[data-country-popup-select]'
        );
        self.countryPopupLanguagesSelect = self.countryPopupWrapper.querySelector(
          '[data-country-popup-languages-select]'
        );
        const vatSwitcher = self.countryPopupWrapper.querySelector('[data-vat-country-switcher]');

        if (vatSwitcher) {
          self.vatInput = vatSwitcher.querySelector('[name="showVatCountryPopup"]');
          self.elemOn = vatSwitcher.querySelector('[data-vat-country-popup-status-on]');
          self.elemOff = vatSwitcher.querySelector('[data-vat-country-popup-status-off]');
          self.showVat = self.vatInput.checked;
        }

        // Check country code is given
        if (self.countryCode == '' || self.options.currentCountryCode == '') {
          return;
        }

        // Check if the selected country is in the EU countries array
        let isEuropeanCountry = false;
        self.options.europeanCountries.split(',').forEach((europeanCountry) => {
          if (europeanCountry == self.countryCode) {
            isEuropeanCountry = true;
            return;
          }
        });

        // Check if the selected country is in the US 'region/countries' array
        let isUsCountry = false;
        self.options.usCountries.split(',').forEach((usCountry) => {
          if (usCountry == self.countryCode) {
            isUsCountry = true;
            return;
          }
        });

        // Check if the selected country is in the GCC 'region/countries' array
        let isGccCountry = false;
        self.options.gccCountries?.split(',').forEach((gccCountry) => {
          if (gccCountry == self.countryCode) {
            isGccCountry = true;
            return;
          }
        });

        // Guard: if the given countryCode is the same as the shop country code or the current country code is EU/US and the countryCode matches one of the EU/US countries then skip!
        if (
          !self.forceDisplay &&
          (self.countryCode == self.options.currentCountryCode ||
            (self.options.currentCountryCode == 'EU' && isEuropeanCountry) ||
            (self.options.currentCountryCode == 'GCC' && isGccCountry) ||
            (self.options.currentCountryCode == 'US' && isUsCountry))
        ) {
          return;
        }

        let countryData;
        // Get the data for EU countries, US countries, GCC countries, or by country code if available.
        // Always check for a specific country code first, then for EU, GCC, and US countries
        if (
          typeof self.options.popupMessages[self.countryCode] != 'undefined' ||
          (isEuropeanCountry && typeof self.options.popupMessages['EU'] != 'undefined') ||
          (isGccCountry && typeof self.options.popupMessages['GCC'] != 'undefined') ||
          (isUsCountry && typeof self.options.popupMessages['US'] != 'undefined') ||
          typeof self.options.popupMessages[self.countryCode] != 'undefined'
        ) {
          if (typeof self.options.popupMessages[self.countryCode] != 'undefined') {
            countryData = self.options.popupMessages[self.countryCode];
          } else if (isEuropeanCountry && typeof self.options.popupMessages['EU'] != 'undefined') {
            countryData = self.options.popupMessages['EU'];
          } else if (isGccCountry && typeof self.options.popupMessages['GCC'] != 'undefined') {
            countryData = self.options.popupMessages['GCC'];
          } else if (isUsCountry && typeof self.options.popupMessages['US'] != 'undefined') {
            countryData = self.options.popupMessages['US'];
          } else {
            countryData = self.options.popupMessages[self.countryCode];
          }

          // Guard: no country data to render
        } else if (!self.forceDisplay) {
          return;
        } else {
          // Set default countryData when forceDisplay is true
          countryData = {
            countryTitle: 'Welcome',
            countryBody: 'Please select your country and language.',
            countryStay: 'Stay on this site',
          };
        }

        // Set the pre-selected value on the Country select according to the current country code
        Array.from(self.countryPopupSelect.options).forEach((option, index) => {
          if (option.dataset.countryCode === self.countryCode) {
            self.countryPopupSelect.selectedIndex = index;
          }
        });

        // Populate the wrapper with the country data
        self.countryPopupWrapper.querySelector('[data-country-popup-title]').innerHTML =
          countryData.countryTitle;
        self.countryPopupWrapper.querySelector('[data-country-popup-text]').innerHTML =
          countryData.countryBody;
        if (self.countryPopupWrapper.querySelector('[data-country-popup-stay]')) {
          self.countryPopupWrapper.querySelector('[data-country-popup-stay]').value =
            countryData.countryStay;
        }

        // Update the available languages based on the selected country
        const selectedCountryOption = self.updateAvailableLanguages();

        // Show or hide the VAT switcher based on the country
        if (
          vatSwitcher &&
          vatSwitcher
            .getAttribute('data-vat-countries')
            .split(',')
            .includes(selectedCountryOption.dataset.countryCode)
        ) {
          vatSwitcher.classList.remove('hidden');
        } else if (vatSwitcher) {
          vatSwitcher.classList.add('hidden');
        }

        // Update the confirm button URL
        self.updateConfirmButtonUrl();

        // Update the VAT switcher options
        if (vatSwitcher) self.vatSwitcherUpdate();

        // Adjusted Event Listeners
        // Only call updateAvailableLanguages when the country select changes
        if (self.countryPopupSelect) {
          self.countryPopupSelect.addEventListener('change', () => {
            const selectedCountryOption = self.updateAvailableLanguages();

            // Show or hide the VAT switcher based on the country
            if (
              vatSwitcher &&
              vatSwitcher
                .getAttribute('data-vat-countries')
                .split(',')
                .includes(selectedCountryOption.dataset.countryCode)
            ) {
              vatSwitcher.classList.remove('hidden');
            } else if (vatSwitcher) {
              vatSwitcher.classList.add('hidden');
            }

            // Update the confirm button URL
            self.updateConfirmButtonUrl();

            // Update the VAT switcher options
            if (vatSwitcher) self.vatSwitcherUpdate();
          });
        }

        // When the language select changes, only update the confirm button URL and VAT switcher
        if (self.countryPopupLanguagesSelect) {
          self.countryPopupLanguagesSelect.addEventListener('change', () => {
            // Update the confirm button URL
            self.updateConfirmButtonUrl();

            // Update the VAT switcher options
            if (vatSwitcher) self.vatSwitcherUpdate();
          });
        }

        // Set URL parameter so the VAT is shown or not on the website the user is redirected to
        self.vatInput?.addEventListener('change', () => {
          self.showVat = !self.showVat;
          self.vatSwitcherUpdate();
        });

        // Open fancybox and set callback for destroy event
        Fancybox.show(
          [
            {
              src: self.countryPopupWrapper,
              type: 'html',
            },
          ],
          {
            mainClass: 'fancybox--is-country-popup',
            animated: false,
            on: {
              destroy: () => {
                self.closeCountryPopup();
              },
            },
          }
        );

        // Set enable cookie
        Cookies.set(self.countryPopupCookieIdentifier, true, {
          expires: self.options.expiresInDays,
        });
      }

      /**
       * Close country popup
       * @returns void
       */
      closeCountryPopup() {
        // Scope
        const self = this;

        Cookies.set(self.countryPopupCookieIdentifier, false, {
          expires: self.options.expiresInDays,
        });

        Fancybox.close();
      }

      /**
       * Update the style of the VAT switcher elements on change & the href of the Confirm button with a param accordingly
       * @returns void
       */
      vatSwitcherUpdate() {
        // Scope
        const self = this;

        // Update styles according to showVat input
        self.elemOn.classList.toggle('text-primary', self.showVat);
        self.elemOn.classList.toggle('text-primary-300', !self.showVat);
        self.elemOff.classList.toggle('text-primary', !self.showVat);
        self.elemOff.classList.toggle('text-primary-300', self.showVat);

        // Add or update the param according to the showVat input
        const confirmButton = self.countryPopupWrapper.querySelector('[data-country-popup-url]');
        const url = new URL(confirmButton.href);
        url.searchParams.set('include_vat', self.showVat ? 'true' : 'false');
        confirmButton.href = url.toString();
      }

      /**
       * Update URL of the Confirm button according to the Country & Language values in the selects
       * @returns void
       */
      updateConfirmButtonUrl() {
        // Scope
        const self = this;

        const selectedCountryOption =
          self.countryPopupSelect.options[self.countryPopupSelect.selectedIndex];
        const selectedLanguageOption =
          self.countryPopupLanguagesSelect?.options[self.countryPopupLanguagesSelect.selectedIndex];

        // Check for disabled and value
        const useLanguageCode = selectedLanguageOption?.value && !selectedLanguageOption.disabled;

        let languageUrl;
        if (selectedLanguageOption?.value === 'en') {
          // Set to blank string if en and enabled
          languageUrl = '';
        } else if (useLanguageCode) {
          // Use value only if enabled (other languages)
          languageUrl = `/${selectedLanguageOption.value}`;
        } else {
          // No language code (disabled or no value)
          languageUrl = '';
        }

        // Build the URL with the domain URL, language code, and current request path
        self.countryPopupWrapper.querySelector(
          '[data-country-popup-url]'
        ).href = `${selectedCountryOption?.value}${languageUrl}`;
      }

      /**
       * Show the country popup manually
       */
      showPopup() {
        // Set the cookie to true to bypass the check
        this.countryPopupCookie = true;

        // Optionally, reset the cookie to allow immediate display
        Cookies.set(this.countryPopupCookieIdentifier, true);

        // Set forceDisplay to true to override guard conditions
        this.forceDisplay = true;

        // Directly call renderCountryPopup
        this.getLocation((countryCode) => {
          this.renderCountryPopup(countryCode);

          // Reset forceDisplay after rendering
          this.forceDisplay = false;
        });
      }
    }

    window.CountryPopup = CountryPopup;
    customElements.define('country-popup', CountryPopup);
  }

  // Select the single element with the data-show-country-popup attribute
  const popupTrigger = document.querySelector('[data-show-country-popup]');

  // Check if the element exists before adding the event listener
  if (popupTrigger) {
    popupTrigger.addEventListener('click', function () {
      const countryPopupElement = document.querySelector('country-popup');
      if (countryPopupElement) countryPopupElement.showPopup();
    });
  }
})();
